import React, { useState } from 'react';
import { Dialog, DialogContent, CircularProgress, Alert } from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import { LocationType } from '../../types/settingsLocationTypeModel';
import { deleteLocationType } from '../../services/settingsLocationType.api';


interface Props {
  open: boolean;
  item: LocationType | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteLocationTypeModal: React.FC<Props> = ({ open, item, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!item) return;
    setLoading(true);
    setError('');
    try {
      await deleteLocationType(item.id);
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to delete location type.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: '16px', fontFamily: 'DM Sans, sans-serif' } }}
    >
      <DialogContent sx={{ p: 3, textAlign: 'center' }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <AlertTriangle size={24} color="#dc2626" />
        </div>

        <div style={{ fontFamily: 'DM Sans', fontWeight: 700, fontSize: 16, color: 'var(--text-primary)', marginBottom: 6 }}>
          Delete Location Type?
        </div>
        <div style={{ fontFamily: 'DM Sans', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: error ? 12 : 22 }}>
          Are you sure you want to delete{' '}
          <strong style={{ color: 'var(--text-primary)' }}>{item?.name}</strong>? This action cannot be undone.
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 2, fontFamily: 'DM Sans', fontSize: 12, borderRadius: '10px', textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              background: 'var(--bg-muted)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '9px 22px',
              fontFamily: 'DM Sans',
              fontWeight: 500,
              fontSize: 13,
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            style={{
              background: '#dc2626',
              border: '1px solid #dc2626',
              borderRadius: 10,
              padding: '9px 22px',
              fontFamily: 'DM Sans',
              fontWeight: 600,
              fontSize: 13,
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 100,
              justifyContent: 'center',
            }}
          >
            {loading && <CircularProgress size={13} sx={{ color: '#fff' }} />}
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteLocationTypeModal;